"use client";

import React, { useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { 
  Utensils, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  MessageSquare, 
  Calendar, 
  Phone, 
  User, 
  Mail, 
  ShieldCheck, 
  Clock, 
  Copy, 
  Check, 
  ChefHat, 
  Wine,
  ArrowRight,
  RotateCcw,
  QrCode,
  Star
} from "lucide-react";
import Link from "next/link";
import QRCode from "qrcode";

function TableDinerContent() {
  const searchParams = useSearchParams();
  const tableParam = searchParams.get("table") || "T-04";
  const restaurantParam = searchParams.get("restaurant") || "The Smoke Co.";

  // Form states
  const [guestName, setGuestName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState<number>(5);
  const [feedback, setFeedback] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false); // MANDATORY UNTICKED BY DEFAULT (DPDP Act 2023)

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentError, setConsentError] = useState(false);

  // Success state
  const [submittedData, setSubmittedData] = useState<any | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [voucherQrDataUrl, setVoucherQrDataUrl] = useState<string>("");

  React.useEffect(() => {
    if (submittedData?.voucher?.code && typeof window !== "undefined") {
      const redeemUrl = `${window.location.origin}/demos/restaurant/redeem?code=${encodeURIComponent(submittedData.voucher.code)}`;
      QRCode.toDataURL(redeemUrl, {
        width: 320,
        margin: 1,
        color: {
          dark: "#0F172A",
          light: "#FFFFFF",
        },
      })
        .then((url) => setVoucherQrDataUrl(url))
        .catch((err) => console.warn("Voucher QR generation error:", err));
    }
  }, [submittedData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setConsentError(false);

    if (!guestName.trim()) {
      setError("Please enter your name.");
      return;
    }

    const cleanDigits = phone.replace(/\D/g, "");
    if (cleanDigits.length < 10) {
      setError("Please enter a valid 10-digit Indian mobile number.");
      return;
    }

    // Strict DPDP & Meta affirmative consent requirement
    if (!marketingConsent) {
      setConsentError(true);
      setError("Explicit consent is required to issue your VIP appetizer voucher via WhatsApp.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/demos/restaurant/table", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurant_name: restaurantParam,
          guest_name: guestName.trim(),
          phone: cleanDigits,
          dob: dob || null,
          email: email.trim() || null,
          table_number: tableParam,
          rating: rating,
          feedback: feedback.trim() || null,
          marketing_consent: marketingConsent,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to issue dining voucher.");
      }

      setSubmittedData(data);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const handleReset = () => {
    setSubmittedData(null);
    setGuestName("");
    setPhone("");
    setDob("");
    setEmail("");
    setMarketingConsent(false);
    setError(null);
    setConsentError(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4 sm:p-6 antialiased selection:bg-amber-500 selection:text-slate-950">
      {/* Background ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[550px] h-[350px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[450px] h-[350px] bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md my-auto">
        {/* Top Restaurant Branding */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold tracking-wide uppercase mb-3">
            <ChefHat className="w-3.5 h-3.5" />
            <span>Table Dine-In VIP Rewards</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-serif tracking-tight text-white flex items-center justify-center gap-2">
            <span>{restaurantParam}</span>
          </h1>

          <div className="flex items-center justify-center gap-2 mt-1 text-xs text-slate-400">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Indiranagar, Bangalore</span>
            <span>•</span>
            <span className="font-semibold text-amber-300 bg-amber-950/60 px-2 py-0.5 rounded border border-amber-800/40">
              {tableParam}
            </span>
          </div>
        </div>

        {/* Card Container */}
        <div className="relative bg-slate-900/90 border border-slate-800/80 rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
          {!submittedData ? (
            /* ================= THE 10-SECOND FORM ================= */
            <div>
              {/* Reward Hook */}
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent border border-amber-500/25">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0 text-amber-400 mt-0.5">
                    <Utensils className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold text-amber-400">
                      Table Welcome Treat
                    </div>
                    <div className="text-sm font-medium text-slate-100 mt-0.5">
                      Complimentary Chef's Bruschetta
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      Instantly unlocks on your phone. Show code to server when ordering.
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                    Your Name <span className="text-amber-400">*</span>
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      required
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="e.g. Vikram Malhotra"
                      className="w-full bg-slate-950/80 border border-slate-800 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/60 transition"
                    />
                  </div>
                </div>

                {/* WhatsApp Phone */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                    WhatsApp Number <span className="text-amber-400">*</span>
                  </label>
                  <div className="relative flex">
                    <div className="inline-flex items-center px-3 rounded-l-xl bg-slate-950 border border-r-0 border-slate-800 text-xs font-semibold text-slate-400">
                      🇮🇳 +91
                    </div>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="98801 94821"
                      className="w-full bg-slate-950/80 border border-slate-800 rounded-r-xl px-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/60 transition"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Your digital voucher is instantly sent to this WhatsApp number.
                  </p>
                </div>

                {/* Birthday (Optional - Fast flow) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Birthday
                    </label>
                    <span className="text-[10px] text-amber-400/80 font-medium">
                      Optional • Free Annual Dessert
                    </span>
                  </div>
                  <div className="relative">
                    <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      className="w-full bg-slate-950/80 border border-slate-800 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/60 transition [color-scheme:dark]"
                    />
                  </div>
                </div>

                {/* Email (Optional) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      Email
                    </label>
                    <span className="text-[10px] text-slate-500">Optional</span>
                  </div>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vikram@example.com"
                      className="w-full bg-slate-950/80 border border-slate-800 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/60 transition"
                    />
                  </div>
                </div>

                {/* Dining Feedback & Star Rating */}
                <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      <span>Rate Your Experience</span>
                    </label>
                    <span className="text-[10px] text-amber-400 font-bold">
                      {rating === 5 ? "Exceptional (5/5)" : `${rating}/5 Stars`}
                    </span>
                  </div>
                  <div className="flex gap-2 justify-center py-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className={`p-1.5 rounded-lg transition ${
                          star <= rating
                            ? "text-amber-400 hover:scale-110"
                            : "text-slate-700 hover:text-slate-500"
                        }`}
                      >
                        <Star className={`w-6 h-6 ${star <= rating ? "fill-amber-400" : ""}`} />
                      </button>
                    ))}
                  </div>

                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">
                      Feedback for Chef / Floor Manager (Optional)
                    </label>
                    <input
                      type="text"
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="e.g. Loved the food & cocktails! Great hospitality."
                      className="w-full bg-slate-900/90 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-amber-500/60"
                    />
                  </div>
                </div>

                {/* MANDATORY CONSENT CHECKBOX - UNTICKED BY DEFAULT (DPDP Act 2023) */}
                <div className={`p-3 rounded-xl border transition ${
                  consentError 
                    ? "bg-red-500/10 border-red-500/40 text-red-300" 
                    : "bg-slate-950/60 border-slate-800 text-slate-300"
                }`}>
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => {
                        setMarketingConsent(e.target.checked);
                        if (e.target.checked) setConsentError(false);
                      }}
                      className="mt-0.5 w-4 h-4 rounded border-slate-700 text-amber-500 focus:ring-amber-500/30 focus:ring-offset-slate-950 bg-slate-900 cursor-pointer"
                    />
                    <span className="text-xs leading-relaxed">
                      I agree to receive VIP dining vouchers, birthday treats, and table updates from <strong>{restaurantParam}</strong> via WhatsApp. I can reply <strong>STOP</strong> anytime to unsubscribe.
                    </span>
                  </label>
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-800/80 text-[10px] text-slate-500">
                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                    <span>DPDP Act 2023 Compliant • No third-party sharing • Reply STOP to revoke</span>
                  </div>
                </div>

                {/* Submit CTA */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-sm tracking-wide shadow-lg shadow-amber-500/20 active:scale-[0.98] transition flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                      <span>Generating VIP Voucher...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Unlock Complimentary Appetizer</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          ) : (
            /* ================= VOUCHER ISSUED SUCCESS VIEW ================= */
            <div className="text-center py-2 animate-in fade-in zoom-in-95 duration-300">
              {/* Celebratory Icon */}
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <h2 className="text-xl font-serif font-bold text-white mb-1">
                {submittedData.message}
              </h2>
              <p className="text-xs text-slate-400 mb-5">
                Present this pass to your server or bartender to claim your treat.
              </p>

              {/* The Acrylic Stand Style Luxury Voucher Card */}
              <div className="relative rounded-2xl bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 border-2 border-amber-500/50 p-5 mb-5 shadow-2xl text-center overflow-hidden">
                {/* Gold rim accent */}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500" />

                {/* Brand Header */}
                <div className="flex items-center justify-center gap-1.5 text-amber-400 text-sm font-bold font-serif mb-1 mt-1">
                  <ChefHat className="w-4 h-4" />
                  <span>{restaurantParam}</span>
                </div>

                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">
                  VIP Table Rewards
                </div>

                {/* QR Code Container */}
                <div className="bg-white p-3 rounded-xl shadow-2xl mx-auto w-[175px] h-[175px] flex items-center justify-center border border-slate-200">
                  {voucherQrDataUrl ? (
                    <img
                      src={voucherQrDataUrl}
                      alt="Redeem QR Code"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>

                {/* Offer Callout */}
                <div className="mt-3">
                  <div className="text-xs sm:text-sm font-bold text-amber-300 uppercase tracking-tight">
                    Scan For Free Appetizer
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Point phone camera at QR code
                  </div>
                </div>

                {/* Footer of the stand card */}
                <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Scan & Dine</span>
                  <span className="font-mono font-bold text-amber-400 bg-amber-950/60 px-2.5 py-0.5 rounded border border-amber-800/40">
                    {submittedData.guest.tableNumber || tableParam}
                  </span>
                </div>
              </div>

              {/* Voucher Code Banner */}
              <div className="mb-4 p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between text-left">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                      Voucher Code
                    </span>
                    <span className="px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      Active
                    </span>
                  </div>
                  <div className="text-lg font-mono font-black text-amber-400 tracking-wider mt-0.5">
                    {submittedData.voucher.code}
                  </div>
                </div>

                <div className="flex gap-1.5">
                  <button
                    onClick={() => handleCopyCode(submittedData.voucher.code)}
                    className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition flex items-center gap-1 text-xs"
                    title="Copy Code"
                  >
                    {copiedCode ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400 text-[11px] font-semibold">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span className="text-[11px]">Copy</span>
                      </>
                    )}
                  </button>

                  <a
                    href={`/api/demos/restaurant/vouchers/qr?code=${encodeURIComponent(submittedData.voucher.code)}&table=${encodeURIComponent(submittedData.guest.tableNumber || tableParam)}&restaurant=${encodeURIComponent(restaurantParam)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-xs font-semibold flex items-center gap-1 transition"
                    title="View Stand Image Pass"
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    <span className="text-[11px]">Pass Image</span>
                  </a>
                </div>
              </div>

              {/* Details Footer */}
              <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-400 bg-slate-950/60 p-3 rounded-xl border border-slate-800/80 mb-4 text-left">
                <div>
                  <span className="text-slate-500">Issued To:</span>{" "}
                  <span className="text-slate-200 font-medium">{submittedData.guest.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-slate-500">Table:</span>{" "}
                  <span className="text-amber-300 font-semibold">{submittedData.guest.tableNumber || tableParam}</span>
                </div>
                <div>
                  <span className="text-slate-500">Validity:</span>{" "}
                  <span className="text-emerald-400 font-medium">7 Days Dine-In</span>
                </div>
                <div className="text-right">
                  <span className="text-slate-500">Visits:</span>{" "}
                  <span className="text-slate-200 font-medium">Visit #{submittedData.guest.visitCount}</span>
                </div>
              </div>

              {/* Server Scan Simulation Link */}
              <div className="text-center -mt-1 mb-4">
                <Link
                  href={`/demos/restaurant/redeem?code=${encodeURIComponent(submittedData.voucher.code)}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-xs text-amber-400/90 hover:text-amber-300 underline font-medium transition"
                >
                  <span>Simulate Server Camera Scan →</span>
                </Link>
              </div>

              {/* WhatsApp Delivery Channel Notice & In-Chat Preview */}
              <div className="p-3.5 rounded-xl bg-slate-950/90 border border-slate-800/80 text-left mb-5">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-200 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
                    <span>WhatsApp VIP Delivery</span>
                  </div>
                  <span className="text-[10px] text-emerald-400 font-mono">Meta Verified</span>
                </div>

                <p className="text-[11px] text-slate-400 leading-relaxed mb-3">
                  {submittedData.delivery.channel === "meta_template" ? (
                    <span className="text-emerald-300 font-medium">
                      ✅ Official WhatsApp voucher dispatched to {submittedData.guest.phone}. Show the stand pass or this screen to your server.
                    </span>
                  ) : submittedData.delivery.channel === "meta_session_window" ? (
                    <span className="text-emerald-300 font-medium">
                      ✅ VIP Acrylic Stand pass image delivered directly to WhatsApp ({submittedData.guest.phone}).
                    </span>
                  ) : (
                    <span className="text-amber-300/90 font-medium">
                      📱 Demo Mode: Stylized VIP Stand pass dispatched to WhatsApp recipient & logged to Floor CRM.
                    </span>
                  )}
                </p>

                {/* Simulated In-Chat Bubble Preview */}
                <div className="p-2.5 rounded-lg bg-[#0b141a] border border-emerald-950/60 text-[11px] text-slate-200">
                  <div className="flex items-center gap-1.5 text-emerald-400 font-bold text-[10px] mb-1">
                    <span>💬 WhatsApp Chat Message</span>
                    <span className="text-slate-500 font-normal">• Just now</span>
                  </div>
                  <div className="text-slate-300 leading-tight">
                    🎉 <strong>Welcome to {restaurantParam}, {submittedData.guest.name}!</strong><br />
                    🎁 <strong>Offer:</strong> Complimentary Chef's Bruschetta<br />
                    🎟️ <strong>Code:</strong> <span className="font-mono text-amber-300">{submittedData.voucher.code}</span><br />
                    📍 <strong>Table:</strong> {submittedData.guest.tableNumber || tableParam}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Link
                  href="/demos/restaurant/crm"
                  className="w-full py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-medium text-xs flex items-center justify-center gap-1.5 transition"
                >
                  <span>Open Floor Manager CRM</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>

                <button
                  onClick={handleReset}
                  className="text-xs text-slate-500 hover:text-slate-400 py-1 transition flex items-center justify-center gap-1 mx-auto"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Test another table scan</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="text-center mt-6 text-[11px] text-slate-500 flex items-center justify-center gap-2">
          <span>Powered by Johnson Softwares VIP Automation</span>
          <span>•</span>
          <Link href="/demos/restaurant" className="hover:text-amber-400 transition underline underline-offset-2">
            Restaurant Demo Hub
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function RestaurantTablePage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-amber-400 text-sm">
        Loading table menu...
      </div>
    }>
      <TableDinerContent />
    </Suspense>
  );
}
